import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface PWAState {
  needRefresh: boolean;
  offlineReady: boolean;
  isInstalled: boolean;
  canInstall: boolean;
  updateServiceWorker: () => Promise<void>;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  closeRefreshPrompt: () => void;
  closeOfflineReady: () => void;
}

export function usePWA(): PWAState {
  const [isInstalled, setIsInstalled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  });

  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);
  const installRef = useRef<InstallPromptEvent | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => {
          registration.update().catch(() => undefined);
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Error registrando SW:', error);
    },
  });

  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const evt = e as InstallPromptEvent;
      installRef.current = evt;
      setInstallEvent(evt);
    };

    const handleInstalled = () => {
      setIsInstalled(true);
      installRef.current = null;
      setInstallEvent(null);
    };

    const mediaQuery = window.matchMedia?.('(display-mode: standalone)');
    const handleDisplayChange = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    mediaQuery?.addEventListener?.('change', handleDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      mediaQuery?.removeEventListener?.('change', handleDisplayChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    const evt = installRef.current;
    if (!evt) return 'unavailable';
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      installRef.current = null;
      setInstallEvent(null);
      return choice.outcome;
    } catch {
      return 'dismissed';
    }
  }, []);

  const closeRefreshPrompt = useCallback(() => setNeedRefresh(false), [setNeedRefresh]);
  const closeOfflineReady = useCallback(() => setOfflineReady(false), [setOfflineReady]);

  return {
    needRefresh,
    offlineReady,
    isInstalled,
    canInstall: !!installEvent && !isInstalled,
    updateServiceWorker: async () => {
      await updateServiceWorker(true);
    },
    promptInstall,
    closeRefreshPrompt,
    closeOfflineReady,
  };
}